<template>
  <avs-card class="pb-4">
    <template v-slot:title>
      <v-toolbar
        class="mt-4 custom-toolbar"
        elevation="0"
      >
        <v-text-field
          class="mr-8"
          clearable
          hide-details
          prepend-inner-icon="mdi-magnify"
          :label="$t('commons.search')"
          v-model="search"
        />
        <avs-createButton
          class="d-flex justify-end"
          @click.stop="openEditView(-1)"
          :name="$t('actions.new', { entity: $t('commons.template').toLowerCase() })"
          :spantext="$t('actions.create', { entity: $t('commons.template').toLowerCase() })"
        />
      </v-toolbar>
    </template>
    <template v-slot:content>
      <v-card-text>
        <v-alert
          color="secondary"
          type="info"
          outlined
          dismissible
        >
          <div class="text-h6">{{ $t("commons.tips") }}</div>
          <div>{{ $t("messages.information.double_clicks") }}</div>
          <div>{{ $t("views.template.published_item_edit") }}</div>
        </v-alert>
      </v-card-text>

      <v-data-table
        class="ma-10"
        dense
        :headers="headers"
        :items="items"
        :items-per-page="-1"
        :search="search"
        :expanded.sync="expanded"
        :single-expand="true"
        item-key="id"
        @dblclick:row="($event, { item }) => openEditView(item.id)"
      >
        <template #item.created_at="{ item }">
          {{ toDatetime(item.created_at) }}
        </template>
        <template #item.type.label="{ item }">
          {{ get_type(item.type.label) }}
        </template>
        <template v-slot:item.status="{ item }">
          <avs-chip :label="false">
            {{ get_status(item.status) }}
          </avs-chip>
        </template>
        <template v-slot:item.actions="{ item }">
          <div class="d-flex justify-end">
            <avs-button
              fab
              icon
              label
              small
              :disabled="item.status == 'LOCKED'"
              :color="!isPublished(item) ? 'success' : 'error'"
              @click="handleOpenDialog(true, item, false)"
              :iconI="!isPublished(item) ? 'mdi-file-check-outline' : 'mdi-file-cancel-outline'"
              :spantext="
                !isPublished(item)
                  ? $t('actions.publish', { entity: $t('commons.template').toLowerCase() })
                  : $t('actions.revoke', { entity: $t('commons.template').toLowerCase() })
              "
            />
            <avs-editButton
              :disabled="!allow.edit(item)"
              @click.stop="openEditView(item.id)"
              :spantext="$t('actions.edit', { entity: $t('commons.template').toLowerCase() })"
            />
            <avs-duplicateButton
              @click.stop="duplicateTemplate(item)"
              :spantext="$t('actions.duplicate', { entity: $t('commons.template').toLowerCase() })"
              :disabled="item.status == 'LOCKED'"
            />

            <avs-deleteButton
              :disabled="!allow.delete(item)"
              @click.stop="handleOpenDialog(true, item, true)"
              :spantext="$t('actions.delete', { entity: $t('commons.template').toLowerCase() })"
            />
          </div>
        </template>
      </v-data-table>

      <avs-deleteDialog
        v-model="deleteDialogIsOpen"
        @cancel="handleOpenDialog(false, null, true)"
        @delete="handleDelete"
        resource="template"
      />
      <ConfirmDialog
        v-model="confirmDialogIsOpen"
        @cancel="handleOpenDialog(false, null, false)"
        @confirm="handleAction"
        :display="display"
      />
    </template>
  </avs-card>
</template>

<script>
import { toDatetime } from "@/utils/datetime"
import { TemplateService } from "@/services/documents"
import { STATE_ENUM } from "@/utils/consts"

import ConfirmDialog from "@/components/generics/dialogs/ConfirmDialog.vue"

export default {
  name: "TemplateListView",
  components: { ConfirmDialog },
  created() {
    this.fetchTemplates()
  },
  data() {
    return {
      allow: {
        edit: v => v.state.id === STATE_ENUM.EDITABLE.id || v.state.id === STATE_ENUM.REVOKED.id,
        delete: v => v.state.id === STATE_ENUM.EDITABLE.id
      },

      expanded: [],
      headers: [
        // { text: "ID", value: "id", sortable: false, filterable: false },
        {
          text: this.$t("categories.company.name"),
          value: "company.name",
          class: "min-width-150",
          sortable: true,
          filterable: false
        },
        {
          text: this.$t("commons.name"),
          value: "name",
          class: "min-width-150",
          sortable: true
        },
        {
          text: this.$t("commons.type"),
          value: "type.label",
          align: "center",
          sortable: true,
          filterable: false
        },
        {
          text: this.$t("commons.created_the"),
          value: "created_at",
          class: "min-width-150",
          sortable: true,
          filterable: false
        },
        {
          text: this.$t("commons.status.name"),
          value: "status",
          align: "center",
          sortable: true,
          filterable: true
        },
        {
          text: this.$t("commons.actions"),
          value: "actions",
          align: "right",
          width: 200,
          sortable: false,
          filterable: false
        }
      ],
      items: [],
      search: "",
      deleteDialogIsOpen: false,
      confirmDialogIsOpen: false,
      selectedItem: null,
      display: {
        title: "",
        text: "",
        prevention: ""
      }
    }
  },
  methods: {
    toDatetime,
    fetchTemplates() {
      let companyId = this.$store.getters["app/getCompany"].pk
      TemplateService.getAll(companyId).then(response => {
        this.items = response
        this.items.map(v => {
          v["status"] = v.state.label
        })
      })
    },
    handleOpenDialog(newState, item, deleteDialog) {
      if (deleteDialog) {
        this.deleteDialogIsOpen = newState
      } else {
        this.confirmDialogIsOpen = newState
        if (item && this.isPublished(item)) {
          this.display = {
            title: this.$t("actions.revoke", {
              entity: this.$t("commons.template").toLowerCase()
            }),
            text: this.$t("views.template.revoke.want_to"),
            prevention: this.$t("views.template.revoke.prevention")
          }
        } else {
          this.display = {
            title: this.$t("actions.publish", {
              entity: this.$t("commons.template").toLowerCase()
            }),
            text: this.$t("views.template.publish.want_to"),
            prevention: this.$t("views.template.publish.prevention")
          }
        }
      }
      this.selectedItem = item
    },
    handleDelete() {
      TemplateService.delete(this.selectedItem.id)
        .then(() => {
          this.fetchTemplates()
          this.send(this.$t("messages.success.operation"), "success")
          this.handleOpenDialog(false, {}, true)
        })
        .catch(error => {
          this.send(error, "error")
        })
    },
    openEditView(index) {
      this.$router.push({ name: "TemplateEditorView", params: { id: index } })
    },
    async duplicateTemplate(item) {
      TemplateService.duplicate(item.id).then(response => {
        this.$store.commit("app/message", { msg: "Template duplicated !", color: "success" })
        this.fetchTemplates()
      })
    },
    isPublished(item) {
      return item.state.id === STATE_ENUM.PUBLISHED.id
    },
    handleAction() {
      if (this.isPublished(this.selectedItem)) {
        this.postProcess(TemplateService.revoke(this.selectedItem.id))
      } else {
        this.postProcess(TemplateService.publish(this.selectedItem.id))
      }
    },
    postProcess(promise) {
      promise
        .then(response => {
          this.fetchTemplates()
          this.send(this.$t("messages.success.operation"), "success")
          this.handleOpenDialog(false, {}, false)
        })
        .catch(reject => {
          const message = this.$t("messages.error.failed_admin", { role: "INSPECTOR" })
          this.$emit("error", message)
        })
    },
    get_status(item) {
      if (item == "EDITABLE") {
        return this.$t("commons.editable")
      } else if (item == "PUBLISHED") {
        return this.$t("commons.published")
      } else if (item == "REVOKED") {
        return this.$t("commons.revoked")
      } else if (item == "SIGNED") {
        return this.$t("commons.signed")
      } else {
        return this.$t("commons.locked")
      }
    },
    get_type(item) {
      if (item == "inspection") {
        return this.$t("commons.inspection")
      }
      return this.$tc("categories.nonconformity.name", 2)
    },
    send(msg, color) {
      this.$store.commit("app/message", { msg, color })
    }
  }
}
</script>
